import React from "react";
import dayjs from "dayjs";
import {
  ScrollContainer,
  StatusBadge,
  StyledTable,
  Td,
  Th,
} from "./TransactionEditCards.styled";
import { Condition } from "../../Cards/Condition";
import { Langugage } from "../../Cards/Language";
import { CardToEditByUserIdCardType } from "magic-shared-types";
import { useAddCommas } from "../../../utils";
import { Box } from "../../primitives";
import { useSelector } from "react-redux";
import { selectGame } from "../../../store/slices/global";

interface TransactionEditCardsProps {
  data: CardToEditByUserIdCardType[];
}

export const TransactionEditCards: React.FC<TransactionEditCardsProps> = ({
  data,
}) => {
  const addCommas = useAddCommas();
  const game = useSelector(selectGame);
  const hasFoil = !["yugioh", "onepiece", "pokemon"].includes(
    game.card_trader_game
  );
  const hasFirstEdition = ["yugioh", "pokemon"].includes(game.card_trader_game);
  const hasReverse = ["pokemon"].includes(game.card_trader_game);

  return (
    <ScrollContainer>
      <StyledTable>
        <thead>
          <tr>
            <Th>ID</Th>
            <Th>Name</Th>
            <Th>Status ct</Th>
            <Th>Blueprint ID</Th>
            <Th>Card Trader ID</Th>
            <Th>Language</Th>
            <Th>Quantity</Th>
            <Th>Condition</Th>
            <Th>Price</Th>
            {hasFoil && <Th>Foil</Th>}
            {hasFirstEdition && <Th>First Edition</Th>}
            {hasReverse && <Th>Reverse</Th>}
            <Th>Signed</Th>
            <Th>Altered</Th>
            <Th>Description</Th>
            <Th>Created At</Th>
            <Th>Error ct</Th>
            <Th>Game</Th>
          </tr>
        </thead>
        <tbody>
          {data?.map((card) => (
            <tr key={card.id}>
              <Td>{card.id}</Td>
              <Td>{card.card_name}</Td>
              <Td>
                <StatusBadge status={card.status_ct}>
                  {card.status_ct}
                </StatusBadge>
              </Td>
              <Td>
                <a
                  href={`https://www.cardtrader.com/cards/${card.blue_print_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {card.blue_print_id}
                </a>
              </Td>
              <Td>{card.cardtrader_id}</Td>
              <Td>
                <Langugage
                  originalLanguage={card.old_language}
                  language={card.new_language}
                />
              </Td>
              <Td>
                {card.old_quantity !== card.new_quantity && (
                  <Box css={{ color: "red", textDecoration: "line-through" }}>
                    {addCommas(card.old_quantity)}
                  </Box>
                )}
                <Box>{addCommas(card.new_quantity)}</Box>
              </Td>
              <Td>
                <Condition
                  originalCondition={card.old_condition}
                  condition={card.new_condition}
                />
              </Td>
              <Td>
                {card.old_price !== card.new_price && (
                  <Box css={{ color: "red", textDecoration: "line-through" }}>
                    {addCommas(card.old_price)} €
                  </Box>
                )}
                <Box>{addCommas(card.new_price)} €</Box>
              </Td>
              {hasFoil && (
                <Td>
                  {card.old_is_foil !== card.new_is_foil && (
                    <Box css={{ color: "red", textDecoration: "line-through" }}>
                      {card.old_is_foil ? "Yes" : "No"}
                    </Box>
                  )}
                  <Box>{card.new_is_foil ? "Yes" : "No"}</Box>
                </Td>
              )}
              {hasFirstEdition && (
                <Td>
                  {card.old_is_first_edition !== card.new_is_first_edition && (
                    <Box css={{ color: "red", textDecoration: "line-through" }}>
                      {card.old_is_first_edition ? "Yes" : "No"}
                    </Box>
                  )}
                  <Box>{card.new_is_first_edition ? "Yes" : "No"}</Box>
                </Td>
              )}
              {hasReverse && (
                <Td>
                  {card.old_is_reverse !== card.new_is_reverse && (
                    <Box css={{ color: "red", textDecoration: "line-through" }}>
                      {card.old_is_reverse ? "Yes" : "No"}
                    </Box>
                  )}
                  <Box>{card.new_is_reverse ? "Yes" : "No"}</Box>
                </Td>
              )}
              <Td>
                {card.old_is_signed !== card.new_is_signed && (
                  <Box css={{ color: "red", textDecoration: "line-through" }}>
                    {card.old_is_signed ? "Yes" : "No"}
                  </Box>
                )}
                <Box>{card.new_is_signed ? "Yes" : "No"}</Box>
              </Td>
              <Td>
                {card.old_is_altered !== card.new_is_altered && (
                  <Box css={{ color: "red", textDecoration: "line-through" }}>
                    {card.old_is_altered ? "Yes" : "No"}
                  </Box>
                )}
                <Box>{card.new_is_altered ? "Yes" : "No"}</Box>
              </Td>
              <Td css={{ width: 200 }}>
                {card.old_description !== card.new_description && (
                  <Box css={{ color: "red", textDecoration: "line-through" }}>
                    {card.old_description}
                  </Box>
                )}
                <Box>{card.new_description}</Box>
              </Td>
              <Td>{dayjs(card.createdAt).format("DD MM HH:mm:ss")}</Td>
              <Td css={{ width: 50 }}>
                {JSON.stringify(card.error_ct, null, 2)}
              </Td>
              <Td>{card.game.display_name}</Td>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </ScrollContainer>
  );
};
